import { ReducerAction, ReducerActionWithPayload } from './App.types';
import capitalize from 'lodash/capitalize';
import { Slide, toast, ToastOptions } from 'react-toastify';
import {
    UserGroup,
    fetchAll,
    refreshTokens,
    fetchWithToken,
    encodeQueryString,
    encodeUrlSearchParams,
} from '@round/api';

export const getLastUpdatedLabel = (lastUpdated: Date, justNowThreshold = 1000 * 60 * 30) => {
    const oneDay = 1000 * 60 * 60 * 24;
    const now = new Date();
    if (now.getTime() - lastUpdated.getTime() <= justNowThreshold) {
        return 'Just now';
    }

    const daysPassed = Math.floor(Math.abs((resetTime(lastUpdated).getTime() - resetTime(now).getTime()) / oneDay));
    return daysPassed === 0 ? 'today' : daysPassed === 1 ? 'yesterday' : `${daysPassed} days ago`;
};

export const formatNumberToCommaNotation = (num: number | string) => {
    return String(num).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export const datePickerFormat = 'MMMM d, yyyy';

export function formatDate(asString: string, options: Intl.DateTimeFormatOptions = {}) {
    // 20 Jan 2020
    const parsed = new Date(asString);
    return parsed.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        ...options,
    });
}

export function formatDateObjShort(d: Date, options: Intl.DateTimeFormatOptions = {}) {
    // 20 Jan
    return d.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
        ...options,
    });
}

export function resetTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

export function roundTo2Dp(x: number) {
    return Math.round(x * 100) / 100;
}

export function numberWithCommas(n: number | string) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const buildTiktokUserUrl = (uniqueId: string) => {
    return `https://www.tiktok.com/@${uniqueId}`;
};

export const buildTiktokMusicUrl = (musicTitle: string, musicTiktokId: string) => {
    const titleWithStrippedPunctuation = musicTitle.replace(/[^\w\s]|_/g, '');
    const titleWithHyphenatedSpaces = titleWithStrippedPunctuation.replace(/ /g, '-');

    return `https://www.tiktok.com/music/${titleWithHyphenatedSpaces}-${musicTiktokId}`;
};

export const buildTiktokPostUrl = (userUniqueId: string, videoId: string) => {
    return `https://www.tiktok.com/@${userUniqueId}/video/${videoId}`;
};

/**
 * Formats snake case string to capitalized
 * @example
 * some_snake_case_input => Some Snake Case Input
 * @param str
 */
export function formatSnakeCaseToDisplay(str: string): string {
    return str
        .replaceAll(/[^a-zA-Z]/g, ' ')
        .split(' ')
        .map(capitalize)
        .join(' ');
}

/**
 * Parsing search params from location.search
 * to an object.
 * @param searchParams
 */
export function parseUrlSearchParams(searchParams: string) {
    const params = new URLSearchParams(searchParams);
    return Array.from(params.entries())
        .filter((entry) => !!entry[1])
        .reduce<Record<string, string>>((acc, value) => {
            acc[value[0]] = value[1];
            return acc;
        }, {});
}

/**
 * Show sliding notification message
 * @param message
 * @param mode - defines notification's style
 */
export function showNotification(message: string, mode: 'info' | 'error' | 'warning') {
    const notificationOptions: ToastOptions = {
        autoClose: 2000,
        hideProgressBar: true,
        position: 'bottom-center',
        style: {
            minHeight: 'auto',
        },
        bodyStyle: {
            paddingTop: 0,
            paddingBottom: 0,
        },
        transition: Slide,
    };

    toast[mode](message, notificationOptions);
}

/**
 * Returns a function which tells if user matches group requirements.
 * @param userGroups - user's groups to test against
 * @returns userHasGroupAccess - A function which tells if user has access given groups to check against and matchType.
 */
export function makeUserHasGroupAccess(
    userGroups: UserGroup[]
): (groups: UserGroup[], hasAccessWhenMatches?: 'all' | 'any') => boolean {
    /**
     * @param groups - list of group requirements
     * @param matchType - check type.
     *  if 'all' is selected, then user groups should contain every group passed,
     *  if match type is 'any' then user should have at least one group from requirements
     */
    return (groups, hasAccessWhenMatches = 'all') => {
        const matches = groups.filter((group) => userGroups.includes(group));
        return hasAccessWhenMatches === 'all' ? matches.length === groups.length : matches.length > 0;
    };
}

export const displayOptionalNumericTableValue = (value: number | undefined | null, emptyValueRepresentation = '-') => {
    return typeof value === 'number' && !isNaN(value) ? numberWithCommas(value) : emptyValueRepresentation;
};

/**
 * Takes a number and returns short version as in example.
 * Limits only up to millions
 * @param n
 * @example
 * formatNumberToKNotation(1000); // 1K
 * formatNumberToKNotation(10_000); // 10K
 * formatNumberToKNotation(1_000_000); // 1M
 * formatNumberToKNotation(1_000_000_000); // 1000M
 */
export const formatNumberToKNotation = (n: number): string => {
    const exponent = Math.floor(Math.log10(Math.abs(n)));
    const sliceRules = [
        { threshold: 3, exponentSign: '', format: (n: number) => n },
        {
            threshold: 4,
            exponentSign: 'K',
            format: (n: number) => {
                const formatted = (Math.floor((n / Math.pow(10, exponent)) * 10) / 10).toFixed(1);
                if (Number(formatted) - Math.floor(Number(formatted)) === 0) {
                    return parseInt(String(formatted));
                }

                return formatted;
            },
        },
        {
            threshold: 6,
            exponentSign: 'K',
            format: (n: number) => Math.round(n).toString(10).slice(0, -3),
        },
        {
            threshold: 7,
            exponentSign: 'M',
            format: (n: number) => {
                const formatted = (Math.floor((n / Math.pow(10, exponent)) * 10) / 10).toFixed(1);
                if (Number(formatted) - Math.floor(Number(formatted)) === 0) {
                    return parseInt(String(formatted));
                }

                return formatted;
            },
        },
        {
            threshold: Infinity,
            exponentSign: 'M',
            format: (n: number) => Math.round(n).toString(10).slice(0, -6),
        },
    ];

    const rule = sliceRules.find((r) => r.threshold > exponent);
    if (!rule) {
        return n.toString();
    }

    return `${rule.format(n)}${rule.exponentSign}`;
};

/**
 * Creates a reducer function that accepts action handlers object
 * where key is action type and value is a reducer function.
 * @param handlers
 */
export function createReducer<State, Actions extends ReducerAction<any> | ReducerActionWithPayload<any, any>>(
    handlers: ActionHandlers<State, Actions>
): (s: State, action: Actions) => State {
    return (state, action) => {
        if (Object.prototype.hasOwnProperty.call(handlers, action.type)) {
            return handlers[action.type as keyof ActionHandlers<State, Actions>](state, action as any);
        }

        return state;
    };
}

export type ActionHandlers<State, Actions extends ReducerAction<any> | ReducerActionWithPayload<any, any>> = {
    [k in Actions['type']]: (state: State, action: Extract<Actions, { type: k }>) => State;
};

export { fetchAll, fetchWithToken, refreshTokens, encodeQueryString, encodeUrlSearchParams };
